import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from '../../auth/AuthProvider';
import { db } from '../../firebase';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { Tabs, Tab } from '../../components/common/Tabs';
import toast from 'react-hot-toast';
import { getJurisdictions, getJurisdictionStates, SUPPORTED_COUNTRIES } from '../../features/jurisdictions/jurisdictionService';
import type { Jurisdiction } from '../../types/jurisdiction';
import { updateCurrentCase } from '../../lib/updateCurrentCase';
import { type CaseTypeId, getCaseTypeDisplayName } from '@/data/tagBank';

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'District of Columbia', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
  'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey',
  'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
  'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah',
  'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
];

const STATE_CODE_MAP: Record<string, string> = {
  'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
  'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'District of Columbia': 'DC',
  'Florida': 'FL', 'Georgia': 'GA', 'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL',
  'Indiana': 'IN', 'Iowa': 'IA', 'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA',
  'Maine': 'ME', 'Maryland': 'MD', 'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN',
  'Mississippi': 'MS', 'Missouri': 'MO', 'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV',
  'New Hampshire': 'NH', 'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY',
  'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH', 'Oklahoma': 'OK', 'Oregon': 'OR',
  'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC', 'South Dakota': 'SD',
  'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT', 'Virginia': 'VA',
  'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY'
};

interface CaseFormData {
  title: string;
  description: string;
  country: string;       // Country code: 'US', 'CA', 'AU'
  state: string;         // State/province display name (e.g., "Oregon", "Ontario")
  stateCode: string;     // State/province code (e.g., "OR", "ON")
  jurisdictionKey: string;  // Composite key: e.g., "US-OR", "CA-ON", "AU-NSW"
  courtType: string;
  caseNumber: string;
  caseTypeId: CaseTypeId | null;  // TAG BANK SYSTEM: Case type for tag scoping
}

export default function NewCasePage() {
  const navigate = useNavigate();
  const { user } = useAuthState();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [jurisdictions, setJurisdictions] = useState<Jurisdiction[]>([]);
  const [loadingJurisdictions, setLoadingJurisdictions] = useState(true);
  
  // Track field-level validation errors
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<CaseFormData>({
    title: '',
    description: '',
    country: 'US',
    state: '',
    stateCode: '',
    jurisdictionKey: '',
    courtType: '',
    caseNumber: '',
    caseTypeId: null,
  });

  // Load jurisdictions once user is authenticated (prevents permission-denied race condition)
  useEffect(() => {
    if (!user) return;
    async function loadJurisdictions() {
      try {
        const data = await getJurisdictions();
        setJurisdictions(data);
      } catch (err) {
        console.error('Failed to load jurisdictions:', err);
        toast.error('Failed to load jurisdictions');
      } finally {
        setLoadingJurisdictions(false);
      }
    }
    loadJurisdictions();
  }, [user]);

  // Load draft from localStorage
  const loadDraft = () => {
    try {
      const draft = localStorage.getItem('newCaseDraft');
      if (draft) {
        const parsed = JSON.parse(draft);
        setFormData(parsed.formData || formData);
        setStep(parsed.step || 0);
      }
    } catch (err) {
      console.error('Failed to load draft:', err);
      toast.error('Failed to load saved draft');
    }
  };

  // Save draft to localStorage
  const saveDraft = () => {
    try {
      localStorage.setItem('newCaseDraft', JSON.stringify({ formData, step }));
    } catch (err) {
      console.error('Failed to save draft:', err);
      toast.error('Failed to save draft');
    }
  };

  const handleSaveDraft = () => {
    saveDraft();
    toast.success('Draft saved! You can continue later.');
    navigate('/cases');
  };

  const handleNext = () => {
    if (step === 0 && !formData.title.trim()) {
      setError('Case title is required');
      toast.error('Case title is required');
      return;
    }
    setError(null);
    setFieldErrors({});
    setStep((prev) => prev + 1);
    saveDraft();
  };

  const handlePrevious = () => {
    setStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    // Prevent double-submission
    if (saving) return;

    if (!user) {
      setError('You must be logged in');
      toast.error('You must be logged in');
      return;
    }

    if (!formData.title.trim()) {
      setError('Case title is required');
      toast.error('Case title is required');
      return;
    }

    // CRITICAL: Block submission if state is missing
    if (!formData.state && !formData.stateCode) {
      setError('State / Province is required');
      setFieldErrors({ state: 'You must select a state or province to create a case' });
      toast.error('Please select a state or province');
      // Navigate back to jurisdiction step
      setStep(1);
      return;
    }

    setSaving(true);
    setError(null);
    setFieldErrors({});

    try {
      // Generate fallback jurisdictionKey if not provided (state-only cases)
      const stateCode = formData.stateCode || STATE_CODE_MAP[formData.state] || formData.state;
      const country = formData.country || 'US';
      const effectiveJurisdictionKey = formData.jurisdictionKey || `${country}-${stateCode}`;

      const caseRef = doc(db, 'cases', `case-${user.uid}-${Date.now()}`);
      await setDoc(caseRef, {
        ownerUid: user.uid,
        partyId: user.uid,
        title: formData.title.trim(),
        title_lc: formData.title.trim().toLowerCase(),
        description: formData.description || '',
        country: country,               // Store country code (e.g., "US", "CA", "AU")
        state: formData.state,          // Store full state/province name
        stateCode: stateCode,           // Store code (e.g., "OR", "ON", "NSW")
        jurisdictionKey: effectiveJurisdictionKey,  // e.g., "US-OR", "CA-ON", "AU-NSW"
        courtType: formData.courtType || 'general',
        caseNumber: formData.caseNumber || '',
        status: 'active',
        memberIds: [user.uid],
        roles: { [user.uid]: 'owner' },
        memberUids: [user.uid],
        counselIds: [],
        evaluatorIds: [],
        counters: { nextExhibitNumber: 1, nextBates: 1 },
        onboardingCompleted: false, // Show wizard on first entry
        caseTypeId: formData.caseTypeId || null,  // TAG BANK SYSTEM: Case type for tag scoping
        activeTags: [],  // TAG BANK SYSTEM: Initially empty
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Clear draft
      localStorage.removeItem('newCaseDraft');

      // Phase 1: Set as current case
      await updateCurrentCase(user.uid, caseRef.id);

      toast.success('Case created successfully');

      // Navigate to the new case
      navigate(`/cases/${caseRef.id}`);
    } catch (err) {
      console.error('Error creating case:', err);
      setError('Failed to create case. Please try again.');
      toast.error('Failed to create case. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Load draft on mount
  useState(() => {
    loadDraft();
  });

  return (
    <div className="container mx-auto px-4 py-6 max-w-4xl text-slate-50">
      <h1 className="text-xl font-semibold mb-6">Create New Case</h1>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-600 text-red-600">
          {error}
        </div>
      )}

      <Tabs active={step} onSelect={setStep}>
        <Tab label="Case Details">
          <CaseDetailsForm formData={formData} setFormData={setFormData} />
        </Tab>
        <Tab label="Jurisdiction">
          <JurisdictionForm
            formData={formData}
            setFormData={setFormData}
            jurisdictions={jurisdictions}
            loadingJurisdictions={loadingJurisdictions}
            error={fieldErrors.jurisdictionKey}
            courtTypeError={fieldErrors.courtType}
            onClearError={() => setFieldErrors(prev => ({ ...prev, jurisdictionKey: '' }))}
            onClearCourtTypeError={() => setFieldErrors(prev => ({ ...prev, courtType: '' }))}
          />
        </Tab>
      </Tabs>

      <div className="flex justify-between mt-8">
        <button onClick={handleSaveDraft} className="btn-outline" aria-label="Save and finish later" data-testid="wizard.saveDraft">
          Save &amp; Finish Later
        </button>
        <div className="flex gap-2">
          {step > 0 && (
            <button className="btn" onClick={handlePrevious} aria-label="Go back to previous step" data-testid="wizard.back">
              ← Case Details
            </button>
          )}
          {step < 1 && (
            <button className="btn-primary" onClick={handleNext} aria-label="Continue to jurisdiction" data-testid="wizard.next">
              Continue to Jurisdiction →
            </button>
          )}
          {step === 1 && (
            <button
              className="btn-accent"
              onClick={handleSubmit}
              disabled={saving}
              aria-label="Create case"
              data-testid="wizard.submit"
            >
              {saving ? 'Creating...' : 'Create Case'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Step 1: Case Details
function CaseDetailsForm({
  formData,
  setFormData,
}: {
  formData: CaseFormData;
  setFormData: (data: CaseFormData) => void;
}) {
  const caseTypes: Array<{ id: CaseTypeId; label: string; description: string }> = [
    { id: 'FAMILY_LAW', label: 'Family Law', description: 'Divorce, custody, support, domestic violence' },
    { id: 'SMALL_CLAIMS', label: 'Small Claims', description: 'Property damage, unpaid debt, contract disputes' },
    { id: 'LANDLORD_TENANT', label: 'Landlord / Tenant', description: 'Eviction, habitability, security deposit' },
    { id: 'CIVIL_HARASSMENT', label: 'Civil Harassment / Restraining Order', description: 'Stalking, harassment, protective orders' },
    { id: 'PROBATE_ESTATE', label: 'Probate / Estate', description: 'Wills, trusts, conservatorship' },
    { id: 'EMPLOYMENT', label: 'Employment / Wage Claim', description: 'Wage theft, discrimination, wrongful termination' },
    { id: 'NAME_CHANGE', label: 'Name Change', description: 'Adult or minor name change petitions' },
    { id: 'GUARDIANSHIP', label: 'Guardianship / Conservatorship', description: 'Minor guardianship, adult conservatorship' },
  ];

  return (
    <div className="space-y-6">
      {/* Case Type Selector */}
      <div>
        <label className="block text-sm font-medium mb-3">
          Case Type <span className="text-slate-400">(optional)</span>
        </label>
        <p className="text-xs text-slate-400 mb-3">
          Select the general legal category that best describes your situation. This helps us load the right guides, forms, and resources for you.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {caseTypes.map((type) => (
            <button
              key={type.id}
              type="button"
              onClick={() => setFormData({ ...formData, caseTypeId: type.id })}
              className={
                `p-4 rounded-lg border-2 text-left transition-all ${
                  formData.caseTypeId === type.id
                    ? 'border-orange-600 bg-orange-600/10'
                    : 'border-slate-700/50 bg-slate-700/30 hover:border-slate-600'
                }`
              }
            >
              <div className="font-medium text-sm mb-1">{type.label}</div>
              <div className="text-xs text-slate-400">{type.description}</div>
            </button>
          ))}
          {/* None option */}
          <button
            type="button"
            onClick={() => setFormData({ ...formData, caseTypeId: null })}
            className={
              `p-4 rounded-lg border-2 text-left transition-all ${
                formData.caseTypeId === null
                  ? 'border-slate-500 bg-slate-600/10'
                  : 'border-slate-700/50 bg-slate-700/30 hover:border-slate-600'
              }`
            }
          >
            <div className="font-medium text-sm mb-1">None / Other</div>
            <div className="text-xs text-slate-400">I'll categorize this later</div>
          </button>
        </div>
      </div>

      <div>
        <label htmlFor="title" className="block text-sm font-medium mb-1">
          Case Title <span className="text-red-600">*</span>
        </label>
        <p className="text-xs text-slate-400 mb-2">Give this case a short name you will recognize later. You can always change it.</p>
        <input
          id="title"
          name="title"
          type="text"
          required
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          className="w-full px-4 py-3 rounded-lg border border-slate-700/50 bg-slate-700 text-slate-50 focus:outline-none focus:ring-2 focus:ring-orange-600"
          placeholder="e.g. Custody — Smith v. Jones"
          data-testid="wizard.case.title"
        />
      </div>

      <div>
        <label htmlFor="description" className="block text-sm font-medium mb-1">
          Description (optional)
        </label>
        <p className="text-xs text-slate-400 mb-2">Briefly describe what this case is about in your own words. Only you and anyone you invite can see this.</p>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={4}
          className="w-full px-4 py-3 rounded-lg border border-slate-700/50 bg-slate-700 text-slate-50 focus:outline-none focus:ring-2 focus:ring-orange-600"
          placeholder="e.g. Seeking a custody modification"
          data-testid="wizard.case.description"
        />
      </div>

    </div>
  );
}

// Step 2: Jurisdiction
function JurisdictionForm({
  formData,
  setFormData,
  jurisdictions,
  loadingJurisdictions,
  error,
  courtTypeError,
  onClearError,
  onClearCourtTypeError,
}: {
  formData: CaseFormData;
  setFormData: (data: CaseFormData) => void;
  jurisdictions: Jurisdiction[];
  loadingJurisdictions: boolean;
  error?: string;
  courtTypeError?: string;
  onClearError?: () => void;
  onClearCourtTypeError?: () => void;
}) {
  const [filteredJurisdictions, setFilteredJurisdictions] = useState<Jurisdiction[]>([]);

  // Dynamic states loaded from Firestore based on selected country
  const [dynamicStates, setDynamicStates] = useState<{ code: string; name: string }[]>([]);
  const [loadingStates, setLoadingStates] = useState(false);

  // Load states from Firestore when country changes
  useEffect(() => {
    const country = formData.country || 'US';
    setLoadingStates(true);
    setDynamicStates([]);

    getJurisdictionStates(country)
      .then((data) => {
        if (data.length > 0) {
          const mapped = data.map((s: any) => ({
            code: s.stateCode || s.state || '',
            name: s.name || s.displayName || s.shortName || s.stateCode || s.state || '',
          })).filter((s: { code: string; name: string }) => s.code && s.name);
          setDynamicStates(mapped);
        } else if (country === 'US') {
          // US fallback: use hardcoded list
          setDynamicStates(US_STATES.map(name => ({
            code: STATE_CODE_MAP[name] || name,
            name,
          })));
        }
      })
      .catch(() => {
        if (country === 'US') {
          setDynamicStates(US_STATES.map(name => ({
            code: STATE_CODE_MAP[name] || name,
            name,
          })));
        }
      })
      .finally(() => setLoadingStates(false));
  }, [formData.country]);

  // Filter county-level jurisdictions when state changes (legacy jurisdictions collection)
  useEffect(() => {
    if (formData.stateCode) {
      const filtered = jurisdictions.filter(j => j.state === formData.stateCode);
      setFilteredJurisdictions(filtered);
    } else if (formData.state) {
      const stateCode = STATE_CODE_MAP[formData.state];
      const filtered = stateCode ? jurisdictions.filter(j => j.state === stateCode) : [];
      setFilteredJurisdictions(filtered);
    } else {
      setFilteredJurisdictions([]);
    }
  }, [formData.state, formData.stateCode, jurisdictions]);

  const handleCountryChange = (value: string) => {
    // Changing country resets state selection
    setFormData({ ...formData, country: value, state: '', stateCode: '', jurisdictionKey: '' });
    if (onClearError) onClearError();
  };

  const handleStateChange = (code: string) => {
    const stateObj = dynamicStates.find(s => s.code === code);
    setFormData({
      ...formData,
      state: stateObj?.name || code,
      stateCode: code,
      jurisdictionKey: '',
    });
    if (onClearError) onClearError();
  };

  const handleJurisdictionChange = (value: string) => {
    setFormData({ ...formData, jurisdictionKey: value });
    if (value && onClearError) onClearError();
  };

  const stateLabel = formData.country === 'AU' ? 'State / Territory' :
                     formData.country === 'CA' ? 'Province / Territory' :
                     'State';

  const statePlaceholder = formData.country === 'AU' ? 'Select a state or territory' :
                           formData.country === 'CA' ? 'Select a province or territory' :
                           'Select a state';

  const stateHelpText = formData.country === 'CA'
    ? 'Select the Canadian province or territory where your legal matter is taking place.'
    : formData.country === 'AU'
    ? 'Select the Australian state or territory where your legal matter is taking place.'
    : 'Select the U.S. state where your legal matter is taking place. If your case spans multiple states, select the state where the case was filed or is most active.';

  return (
    <div className="space-y-4">
      {/* Jurisdiction Explainer */}
      <div className="rounded-lg bg-slate-700/40 border-l-4 border-orange-500 px-4 py-3">
        <p className="text-sm font-semibold text-slate-100 mb-1">Why does this matter?</p>
        <p className="text-sm text-slate-300">Courts, filing deadlines, required forms, and procedural rules vary significantly by jurisdiction. Selecting your country and state/province lets ThreadLock load the right resources, guides, and court forms for your specific situation.</p>
        <p className="mt-2 text-xs text-slate-400 italic">You can update your jurisdiction later from the Case Settings panel if anything changes.</p>
      </div>

      {/* Country Selector */}
      <div>
        <label htmlFor="country" className="block text-sm font-medium mb-2">
          Country <span className="text-red-600">*</span>
        </label>
        <select
          id="country"
          name="country"
          value={formData.country || 'US'}
          onChange={(e) => handleCountryChange(e.target.value)}
          className="w-full px-4 py-3 rounded-lg border border-slate-700/50 bg-slate-700 text-slate-50 focus:outline-none focus:ring-2 focus:ring-orange-600"
          data-testid="wizard.case.country"
        >
          {SUPPORTED_COUNTRIES.map((c) => (
            <option key={c.code} value={c.code}>{c.label}</option>
          ))}
        </select>
      </div>

      {/* State / Province Selector */}
      <div>
        <label htmlFor="state" className="block text-sm font-medium mb-2">
          {stateLabel} <span className="text-red-600">*</span>
        </label>
        <select
          id="state"
          name="state"
          required
          value={formData.stateCode || ''}
          onChange={(e) => handleStateChange(e.target.value)}
          disabled={loadingStates}
          className="w-full px-4 py-3 rounded-lg border border-slate-700/50 bg-slate-700 text-slate-50 focus:outline-none focus:ring-2 focus:ring-orange-600 disabled:opacity-60"
          data-testid="wizard.case.state"
        >
          <option value="">{loadingStates ? 'Loading…' : statePlaceholder}</option>
          {dynamicStates.map((s) => (
            <option key={s.code} value={s.code}>{s.name}</option>
          ))}
        </select>
        <p className="mt-1 text-xs text-slate-400">{stateHelpText}</p>
      </div>

      {/* County/Court Selector - Optional, US-only (legacy jurisdictions collection) */}
      {formData.state && filteredJurisdictions.length > 0 && (
        <div>
          <label htmlFor="jurisdictionKey" className="block text-sm font-medium mb-2">
            County/Court Jurisdiction <span className="text-slate-400">(optional)</span>
          </label>
          <select
            id="jurisdictionKey"
            name="jurisdictionKey"
            value={formData.jurisdictionKey}
            onChange={(e) => handleJurisdictionChange(e.target.value)}
            className="w-full px-4 py-3 rounded-lg border border-slate-700/50 bg-slate-700 text-slate-50 focus:outline-none focus:ring-2 focus:ring-orange-600"
            data-testid="wizard.case.jurisdictionKey"
          >
            <option value="">General - {formData.state}</option>
            {filteredJurisdictions.map((j) => (
              <option key={j.id} value={j.id}>
                {j.displayName}
              </option>
            ))}
          </select>
          <p className="mt-2 text-sm text-slate-400">
            Select a specific court if available. You can update this later.
          </p>
        </div>
      )}

      {/* Court Type - Optional */}
      <div>
        <label htmlFor="courtType" className="block text-sm font-medium mb-2">
          Court Type <span className="text-slate-400">(optional)</span>
        </label>
        <select
          id="courtType"
          name="courtType"
          value={formData.courtType}
          onChange={(e) => {
            setFormData({ ...formData, courtType: e.target.value });
            if (e.target.value && onClearCourtTypeError) {
              onClearCourtTypeError();
            }
          }}
          className="w-full px-4 py-3 rounded-lg border border-slate-700/50 bg-slate-700 text-slate-50 focus:outline-none focus:ring-2 focus:ring-orange-600"
          data-testid="wizard.case.courtType"
        >
          <option value="">General</option>
          <option value="family">Family Court</option>
          <option value="civil">Civil Court</option>
          <option value="criminal">Criminal Court</option>
          <option value="probate">Probate Court</option>
          <option value="other">Other</option>
        </select>
        <p className="mt-2 text-sm text-slate-400">
          Select the type of court if known. You can update this later.
        </p>
      </div>

      {/* Case Number (Optional) */}
      <div>
        <label htmlFor="caseNumber" className="block text-sm font-medium mb-2">
          Case Reference / Number (optional)
        </label>
        <p className="text-xs text-slate-400 mb-2">
          If you have a court-assigned case number, enter it here. If you do not have one yet, leave this blank.
        </p>
        <input
          id="caseNumber"
          name="caseNumber"
          type="text"
          value={formData.caseNumber}
          onChange={(e) => setFormData({ ...formData, caseNumber: e.target.value })}
          className="w-full px-4 py-3 rounded-lg border border-slate-700/50 bg-slate-700 text-slate-50 focus:outline-none focus:ring-2 focus:ring-orange-600"
          placeholder="e.g. 24FL00123"
          data-testid="wizard.case.caseNumber"
        />
      </div>
    </div>
  );
}



